/*
  Stages tab overview (UI overhaul wave 1, screen 04):
  - StagePipeline      — all stages in order; done / next / not started; click a step to jump to it (idea 3)
  - WaitingOnCard      — who holds the ball: items "Pending from …" per party (idea 5)
  - RecentActivityCard — last changes on this project from the history log, for Admin / Super Admin (idea 7)
  Wave 3: StagePipeline shows each stage's clock ("Waiting 2 d", red past its SLA — idea 8).
  Final QA: every step name takes two lines' height (so the status lines under them share one baseline) and the
  activity sub-line is in words ("1 variation deleted", not "variations: 1 deleted").
*/
import { useEffect, useState } from 'react';
import { Check, History, Timer } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatTimeAgo, formatDateTime } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { getStatusTone } from '@/lib/status';
import { StatusPill, ToneDot } from '@/components/ui/status-pill';
import { humanizeUndoLabel } from '@/components/entry/undo-text';
import { humanizeChangeSummary } from '@/components/admin/adminModel';
import { Panel, PanelHead } from './ProjectUi';
import { buildStagePipeline } from './projectDetailModel';
import { describeStageClock } from './stagesModel';

const sentenceStatus = (status) => (status === 'Not Started' ? 'Not started' : status);

// Pipeline labels only (the full name is the tooltip and the button's label): long names in two short lines.
const STEP_SHORT_NAMES = { 'handing over documents': 'Handover docs' };
const stepLabel = (name) => STEP_SHORT_NAMES[String(name || '').trim().toLowerCase()] || name;

/** The activity sub-line in words; '' when it would only repeat the entry's label. */
export function activitySummary(item) {
  const summary = humanizeChangeSummary(item?.change_summary);
  if (!summary) return '';
  const same = (value) => String(value || '').trim().toLowerCase() === summary.trim().toLowerCase();
  return same(item?.label) || same(humanizeUndoLabel(item?.label)) ? '' : summary;
}

const RING_BY_TONE = {
  blue: 'border-status-blue text-status-blue-ink',
  amber: 'border-status-amber text-status-amber-ink',
  red: 'border-status-red text-status-red-ink',
};

export function StagePipeline({ stages = [], waitingByStage = {}, clocks = {}, onSelect, className }) {
  const steps = buildStagePipeline(stages);
  if (!steps.length) return null;

  return (
    <Panel as="nav" aria-label="Stage pipeline" className={cn('px-2 py-4 sm:px-4', className)} data-testid="stage-pipeline">
      <ol className="flex overflow-x-auto pb-1">
        {steps.map((step, index) => {
          const previousDone = index > 0 && steps[index - 1].state === 'done';
          const tone = getStatusTone(step.status, 'stage');
          const waiting = waitingByStage[step.id] || [];
          const current = step.state === 'current';
          const done = step.state === 'done';
          const clockInfo = describeStageClock(clocks[step.id], step.name);
          return (
            <li key={step.id} className="relative flex min-w-[100px] flex-1 flex-col items-center px-0.5 text-center">
              {index > 0 ? (
                <span aria-hidden="true" className={cn('absolute left-0 right-1/2 top-[15px] h-[2px]', previousDone ? 'bg-status-green' : 'bg-line')} />
              ) : null}
              {index < steps.length - 1 ? (
                <span aria-hidden="true" className={cn('absolute left-1/2 right-0 top-[15px] h-[2px]', done ? 'bg-status-green' : 'bg-line')} />
              ) : null}
              <button
                type="button"
                onClick={() => onSelect?.(step.id)}
                aria-current={current ? 'step' : undefined}
                aria-label={`${step.number} ${step.name}: ${step.status}${current ? ' (next)' : ''}`}
                data-testid={`stage-step-${step.id}`}
                className="group relative z-[1] flex w-full flex-col items-center rounded-lg px-0.5 pb-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span
                  className={cn(
                    'grid h-8 w-8 place-items-center rounded-full border text-[11.5px] font-bold tabular-nums transition-transform group-hover:scale-105',
                    done && 'border-status-green bg-status-green text-white',
                    current && 'border-2 border-[color:var(--brand)] bg-surface text-[color:var(--brand-ink)] shadow-[0_0_0_4px_var(--brand-soft)]',
                    !done && !current && (RING_BY_TONE[tone] ? `border-2 bg-surface ${RING_BY_TONE[tone]}` : 'border-line-2 bg-surface text-ink-3'),
                  )}
                >
                  {done ? <Check className="h-4 w-4" strokeWidth={3} aria-hidden="true" /> : step.number}
                </span>
                <span
                  className={cn('mt-2 line-clamp-2 min-h-[2.5em] w-full text-[12px] font-semibold leading-[1.25] [overflow-wrap:anywhere]', current ? 'text-[color:var(--brand-ink)]' : 'text-ink')}
                  title={step.name}
                >
                  {stepLabel(step.name)}
                </span>
                <span
                  className={cn(
                    'mt-0.5 text-[11px] leading-tight',
                    done && 'font-semibold text-[color:var(--brand-ink)]',
                    current && 'font-bold text-[color:var(--brand-ink)]',
                    !done && !current && 'text-ink-3',
                  )}
                >
                  {current ? `Next · ${sentenceStatus(step.status)}` : sentenceStatus(step.status)}
                </span>
              </button>
              {waiting.length ? (
                <StatusPill
                  tone="amber"
                  size="sm"
                  className="mt-1 max-w-none whitespace-nowrap"
                  label={`Waiting · ${waiting.map((entry) => entry.label).join(', ')}`}
                  title={`Waiting on ${waiting.map((entry) => entry.label).join(', ')}`}
                />
              ) : null}
              {clockInfo ? (
                <StatusPill
                  tone={clockInfo.tone}
                  size="sm"
                  icon={Timer}
                  className="mt-1 !h-auto min-h-[20px] max-w-full whitespace-normal rounded-[10px] py-0.5 text-center"
                  label={clockInfo.compact}
                  title={clockInfo.sentence}
                  aria-label={clockInfo.sentence}
                  data-testid={`stage-clock-${step.id}`}
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}

export function WaitingOnCard({ waitingOn, onSelectItem, className }) {
  const parties = waitingOn?.parties || [];
  const total = waitingOn?.total || 0;
  return (
    <Panel className={className} data-testid="stages-waiting-on" aria-labelledby="stages-waiting-on-title">
      <PanelHead
        title={<span id="stages-waiting-on-title">Waiting on</span>}
        meta={total ? `${total} open item${total === 1 ? '' : 's'}` : 'Nothing pending'}
      />
      <ul>
        {parties.map((party) => {
          const count = party.items.length;
          return (
            <li key={party.key} className="flex min-h-[42px] items-center gap-2.5 border-b border-line-2 px-4 py-2 last:border-b-0 sm:px-5">
              <ToneDot tone={count ? 'amber' : 'gray'} />
              <span className={cn('shrink-0 text-[13px]', count ? 'font-semibold text-ink' : 'text-ink-3')}>{party.label}</span>
              {count ? (
                <span className="flex min-w-0 flex-wrap gap-x-1.5 text-[12px] text-ink-3">
                  {party.items.slice(0, 3).map((item) => (
                    <button
                      key={`${item.stageId}-${item.lineId}`}
                      type="button"
                      onClick={() => onSelectItem?.(item)}
                      className="max-w-[220px] truncate rounded hover:text-[color:var(--brand-ink)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      title={`${item.stageName} › ${item.label}`}
                    >
                      {item.label === item.stageName ? item.label : `${item.stageName} · ${item.label}`}
                    </button>
                  ))}
                  {count > 3 ? <span>+{count - 3} more</span> : null}
                </span>
              ) : null}
              <span className={cn('ml-auto text-[13px] font-semibold tabular-nums', count ? 'text-ink' : 'text-ink-3')}>{count}</span>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

const initials = (name) => String(name || '?').trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();

// After a change anywhere on the page, wait for the history record to be written before re-reading it.
const ACTIVITY_REFRESH_DELAY_MS = 900;

export function RecentActivityCard({ projectId, refreshKey, onOpenHistory, className }) {
  const [state, setState] = useState({ loading: true, items: [], error: '' });
  const [mutationTick, setMutationTick] = useState(0);

  // Every successful POST / PUT / PATCH / DELETE (api.js fires app:mutation-success) may be a change to
  // this project — stage items, execution, invoices… — even when the project object itself is untouched.
  useEffect(() => {
    let timer = null;
    const handleMutation = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setMutationTick((tick) => tick + 1), ACTIVITY_REFRESH_DELAY_MS);
    };
    window.addEventListener('app:mutation-success', handleMutation);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('app:mutation-success', handleMutation);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Keep showing the current list while it refreshes; "Loading…" only on the first load.
    setState((prev) => ({ ...prev, loading: prev.items.length === 0, error: '' }));
    api.get('/undo/page-history', {
      params: { page_type: 'project', entity_id: projectId, entity_type: 'project', limit: 4 },
    })
      .then((res) => {
        if (!cancelled) setState({ loading: false, items: (res.data?.items || []).slice(0, 4), error: '' });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, items: [], error: getErrorMessage(err, 'Could not load recent activity') });
      });
    return () => { cancelled = true; };
  }, [projectId, refreshKey, mutationTick]);

  return (
    <Panel className={className} data-testid="stages-recent-activity" aria-labelledby="stages-recent-activity-title">
      <PanelHead title={<span id="stages-recent-activity-title">Recent activity</span>} meta="Latest 4 changes" />
      <div className="px-4 py-2 sm:px-5">
        {state.loading ? (
          <p className="py-3 text-[13px] text-ink-3">Loading activity…</p>
        ) : state.error ? (
          <p className="py-3 text-[13px] text-status-red-ink">{state.error}</p>
        ) : state.items.length === 0 ? (
          <p className="py-3 text-[13px] text-ink-3">No changes recorded on this project yet.</p>
        ) : (
          <ul className="divide-y divide-line-2">
            {state.items.map((item) => {
              const summary = activitySummary(item);
              return (
              <li key={item.id} className="flex items-start gap-2.5 py-2.5">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[color:var(--brand)] text-[10.5px] font-bold text-[color:var(--brand-fg)]" aria-hidden="true">
                  {initials(item.user_name)}
                </span>
                <p className="min-w-0 flex-1 text-[13px] leading-snug text-ink-2">
                  <span className="font-semibold text-ink">{item.user_name || 'Someone'}</span>
                  {' · '}
                  {humanizeUndoLabel(item.label)}
                  {summary ? <span className="block truncate text-[12px] text-ink-3" title={item.change_summary}>{summary}</span> : null}
                </p>
                <time className="shrink-0 text-[12px] text-ink-3" dateTime={item.created_at} title={formatDateTime(item.created_at)}>
                  {formatTimeAgo(item.created_at)}
                </time>
              </li>
              );
            })}
          </ul>
        )}
      </div>
      {onOpenHistory ? (
        <div className="border-t border-line px-4 py-2.5 sm:px-5">
          <button
            type="button"
            onClick={onOpenHistory}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[color:var(--brand-ink)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <History className="h-3.5 w-3.5" aria-hidden="true" />
            Open full history
          </button>
        </div>
      ) : null}
    </Panel>
  );
}
